import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import { BrandingProvider } from "./hooks/useBranding";
import axios from "axios";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeForm from "./pages/EmployeeForm";
import Payroll from "./pages/Payroll";
import Recruitment from "./pages/Recruitment";
import Training from "./pages/Training";
import Attendance from "./pages/Attendance";
import TimeTracking from "./pages/TimeTracking";
import Vacations from "./pages/Vacations";
import AISettings from "./pages/AISettings";
import AIAgent from "./pages/AIAgent";
import Users from "./pages/Users";
import Billing from "./pages/Billing";
import Company from "./pages/Company";
import Companies from "./pages/Companies";
import Organigrama from "./pages/Organigrama";
import PortalColaborador from "./pages/PortalColaborador";
import Documents from "./pages/Documents";
import Onboarding from "./pages/Onboarding";
import Assets from "./pages/Assets";
import Analytics from "./pages/Analytics";
import Reports from "./pages/Reports";
import Helpdesk from "./pages/Helpdesk";
import KnowledgeBase from "./pages/KnowledgeBase";
import Complaints from "./pages/Complaints";
import Performance from "./pages/Performance";
import Surveys from "./pages/Surveys";
import Communications from "./pages/Communications";
import Benefits from "./pages/Benefits";
import Psychometric from "./pages/Psychometric";
import TakeAssessment from "./pages/TakeAssessment";
import SuperAdminPanel from "./pages/SuperAdminPanel";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ChatBubble from "./components/ChatBubble";
import RevenueDashboard from "./pages/RevenueDashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import MobileApp from "./pages/MobileApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const register = async (name, email, password, role = "admin_empresa") => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error(e);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setAuthUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
          withCredentials: true
        });
        setAuthUser(response.data.user);
        navigate('/dashboard', { state: { user: response.data.user }, replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setAuthUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Autenticando...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh", "supervisor"]}><Employees /></ProtectedRoute>} />
      <Route path="/employees/new" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><EmployeeForm /></ProtectedRoute>} />
      <Route path="/employees/:id/edit" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><EmployeeForm /></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Payroll /></ProtectedRoute>} />
      <Route path="/recruitment" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh", "supervisor"]}><Recruitment /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/time-tracking" element={<ProtectedRoute><TimeTracking /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh", "supervisor"]}><Attendance /></ProtectedRoute>} />
      <Route path="/vacations" element={<ProtectedRoute><Vacations /></ProtectedRoute>} />
      <Route path="/ai-settings" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><AISettings /></ProtectedRoute>} />
      <Route path="/ai-agent/:agentType" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><AIAgent /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa"]}><Users /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa"]}><Billing /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa"]}><Company /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute allowedRoles={["super_admin"]}><Companies /></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminPanel /></ProtectedRoute>} />
      <Route path="/revenue" element={<ProtectedRoute allowedRoles={["super_admin"]}><RevenueDashboard /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/organigrama" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh", "supervisor"]}><Organigrama /></ProtectedRoute>} />
      <Route path="/portal" element={<ProtectedRoute><PortalColaborador /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Documents /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Onboarding /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Assets /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Analytics /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Reports /></ProtectedRoute>} />
      <Route path="/helpdesk" element={<ProtectedRoute><Helpdesk /></ProtectedRoute>} />
      <Route path="/knowledge-base" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
      <Route path="/complaints" element={<ProtectedRoute><Complaints /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh", "supervisor"]}><Performance /></ProtectedRoute>} />
      <Route path="/surveys" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Surveys /></ProtectedRoute>} />
      <Route path="/communications" element={<ProtectedRoute><Communications /></ProtectedRoute>} />
      <Route path="/benefits" element={<ProtectedRoute><Benefits /></ProtectedRoute>} />
      <Route path="/psychometric" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa", "gerente_rh"]}><Psychometric /></ProtectedRoute>} />
      <Route path="/mobile" element={<MobileApp />} />
      <Route path="/assessment/:accessCode" element={<TakeAssessment />} />
      <Route path="/settings/billing" element={<ProtectedRoute allowedRoles={["super_admin", "admin_empresa"]}><Billing /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <AppRouter />
          <ImpersonationBanner />
          <ChatBubble />
          <Toaster position="top-right" richColors />
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
